/**
 * Kavee custom JavaScript
 * Import theme components and extend them here.
 *
 * Example - extend the Drawer component:
 *   import { Drawer } from "theme";
 *   class CustomDrawer extends Drawer { ... }
 *   customElements.define('custom-drawer', CustomDrawer);
 */

/**
 * Disable hover scale on shopify-account avatar.
 * .account-button__avatar is inside shadow DOM (no part exposed), so we inject override styles.
 */
function injectAccountAvatarOverride(element) {
  if (!element.shadowRoot || element.dataset.kaveeAvatarOverride) return;
  const style = document.createElement('style');
  style.textContent = '.account-button__avatar:hover{scale:1!important;box-shadow:inset 0 0 0 1px #00000014!important}';
  element.shadowRoot.appendChild(style);
  element.dataset.kaveeAvatarOverride = '1';
}

function observeAccountElements() {
  document.querySelectorAll('shopify-account').forEach(injectAccountAvatarOverride);
}

async function initAccountAvatarOverride() {
  await customElements.whenDefined('shopify-account');
  observeAccountElements();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAccountAvatarOverride);
} else {
  initAccountAvatarOverride();
}

function startObserver() {
  if (!document.body) return;
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType === 1 && (node.matches?.('shopify-account') || node.querySelector?.('shopify-account'))) {
          observeAccountElements();
          break;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.body) startObserver();
else document.addEventListener('DOMContentLoaded', startObserver);

/**
 * Map a Shopify Ajax Cart line item (cart/add.js) to Made With Intent AddToCart `data` fields.
 * @see https://help.madewithintent.ai/en/articles/9454635-the-intent-tag-sdk
 */
function kaveeNormalizeProductImageUrl(url) {
  if (!url || typeof url !== 'string') return '';
  if (url.indexOf('http') === 0) return url;
  if (url.indexOf('//') === 0) return `https:${url}`;
  return url;
}

function kaveeFormatMoneyFromCents(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
    const format = window.themeVariables?.settings?.moneyFormat;
    if (format) return window.Shopify.formatMoney(n, format);
  }
  return (n / 100).toFixed(2);
}

function mapLineItemToMwiDetail(item) {
  if (!item || typeof item !== 'object') return {};
  const unitCents =
    item.final_price != null ? item.final_price : item.price != null ? item.price : null;
  const imageSrc =
    item.featured_image && typeof item.featured_image === 'object' && item.featured_image.url
      ? item.featured_image.url
      : typeof item.image === 'string'
        ? item.image
        : '';
  return {
    product_name: item.product_title || item.title || '',
    product_sku: item.sku || '',
    product_price: kaveeFormatMoneyFromCents(unitCents),
    product_currency:
      (typeof window.Shopify !== 'undefined' && window.Shopify.currency && window.Shopify.currency.active) ||
      '',
    product_brand: item.vendor || '',
    product_category: item.product_type || '',
    product_image_url: kaveeNormalizeProductImageUrl(imageSrc),
    product_availability: 1
  };
}

function dispatchAddToCartFromLineItems(items) {
  if (!window.intent || !window.intent.dataLayer || !items || !items.length) return;
  for (let i = 0; i < items.length; i++) {
    const detail = mapLineItemToMwiDetail(items[i]);
    document.dispatchEvent(new CustomEvent('mwi:add-to-cart', { bubbles: true, detail }));
  }
}

window.kaveeMwi = window.kaveeMwi || {};
window.kaveeMwi.dispatchAddToCartFromLineItems = dispatchAddToCartFromLineItems;

document.addEventListener('variant:add', (e) => {
  const items = e.detail?.items;
  if (!items?.length) return;
  dispatchAddToCartFromLineItems(items);
});

// Alpine countdown component used by countdown sections (reads end_date and expired_message from data attributes)
document.addEventListener('alpine:init', () => {
  Alpine.data('countdownTimer', () => ({
    initialized: false,
    expired: false,
    expiredMessage: 'This offer has ended.',
    hours: '00',
    minutes: '00',
    seconds: '00',
    _end: null,
    _timerId: null,

    start() {
      const endDateString = this.$el.dataset.endDate;
      const msg = this.$el.dataset.expiredMessage;
      if (msg) this.expiredMessage = msg;

      if (!endDateString) {
        return;
      }

      const parsed = new Date(endDateString);

      if (isNaN(parsed.getTime())) {
        // Invalid date string; do not start timer
        return;
      }

      this._end = parsed;
      this.initialized = true;
      this.tick();

      this._timerId = setInterval(() => {
        this.tick();
      }, 1000);
    },

    tick() {
      if (!this._end) return;

      const now = new Date();
      let diff = this._end.getTime() - now.getTime();

      if (diff <= 0) {
        diff = 0;
        this.expired = true;
        this.hours = '00';
        this.minutes = '00';
        this.seconds = '00';

        if (this._timerId) {
          clearInterval(this._timerId);
          this._timerId = null;
        }

        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;

      this.hours = String(h).padStart(2, '0');
      this.minutes = String(m).padStart(2, '0');
      this.seconds = String(s).padStart(2, '0');
    }
  }));
});